import type { HotDeal } from '@/js/services/RogueTypes'

type PromoPriceSource = Pick<HotDeal, 'price' | 'status'> | NonNullable<HotDeal['variations']>[number]

/**
 * The hot deal price that applies to a product card.
 * An active deal's price always applies. A missed deal's price only applies on the deals page, where the card is shown
 * as "Missed Deal" with the price that was missed; every other page ignores it. Upcoming prices never apply.
 *
 * The deals page sorts on this price, and hover-card writes the price it displays into the same product objects
 * (special_price), so both must follow one rule: otherwise the browser re-sorts the server-rendered cards into a
 * different order after hydration.
 *
 * @param showMissed whether the card is shown as a Missed Deal (deals page, and the card's own entry is missed)
 */
export function shownPromoPrice(promo: PromoPriceSource | undefined | null, showMissed: boolean): number | undefined {
  if (!promo) {
    return undefined
  }

  if (promo.status === 'active' || (showMissed && promo.status === 'missed')) {
    return promo.price
  }

  return undefined
}

type PromoStockSource = Pick<HotDeal, 'qty' | 'qtyStart' | 'qtyPctRemaining'>

/**
 * The share of a quantity based hot deal's stock still left, 0 to 1, or undefined for a deal with no stock measure.
 * A deal with a starting quantity counts down from it; otherwise the feed's precomputed share is used.
 */
export function promoRemainingFraction(promo: PromoStockSource | undefined | null): number | undefined {
  if (!promo) {
    return undefined
  }

  if (promo.qtyStart && promo.qty !== undefined) {
    return promo.qty / promo.qtyStart
  }

  return promo.qtyPctRemaining
}

/**
 * The active deal with the least stock left among a product's children, so its bar shows the item closest to selling
 * out rather than whichever child is listed or selected first. Sold-out children are skipped, as are children with
 * no stock measure. Undefined when no child qualifies, so the caller can fall back to its default deal.
 */
export function lowestRemainingPromo(promos: (HotDeal | undefined | null)[]): HotDeal | undefined {
  return promos.reduce<HotDeal | undefined>((lowest, promo) => {
    if (promo?.status !== 'active') {
      return lowest
    }

    const fraction = promoRemainingFraction(promo)
    if (fraction === undefined || fraction <= 0) {
      return lowest
    }

    return !lowest || fraction < (promoRemainingFraction(lowest) as number) ? promo : lowest
  }, undefined)
}

/**
 * The whole percent of a quantity based hot deal's stock still left, for the countdown bar and its label.
 * Rounds down, so the first sale drops the deal from 100% to 99%, but shows 1% rather than 0% while any stock is left.
 *
 * @param fraction the share of the deal's stock left, 0 to 1
 */
export function promoPctRemaining(fraction: number): number {
  if (fraction <= 0) {
    return 0
  }

  // Clear float error before flooring: 29 / 100 * 100 is 28.999…, which would floor to 28
  const pct = Math.floor(Math.round(fraction * 1e8) / 1e6)

  return Math.min(100, Math.max(1, pct))
}
